import React, { createContext, useContext, ReactNode, useState } from 'react';

interface StoryStatus {
  status: string;
  current_step: string;
  has_final_video: boolean;
  character_phase: string;
  character_count: number;
  shots_count: number;
  final_url?: string;
}

interface StoryContextType {
  selectedStoryId: number | null;
  setSelectedStoryId: (id: number | null) => void;
  fetchedDescriptions: Set<string>;
  setFetchedDescriptions: React.Dispatch<React.SetStateAction<Set<string>>>;
  storyStatus: StoryStatus | null;
  storyStatusLoading: boolean;
  storyStatusError: string | null;
  isSwitchingStory?: boolean;
}

const StoryContext = createContext<StoryContextType | undefined>(undefined);

export const useStory = () => {
  const context = useContext(StoryContext);
  if (context === undefined) {
    throw new Error('useStory must be used within a StoryProvider');
  }
  return context;
};

interface StoryProviderProps {
  children: ReactNode;
  selectedStoryId: number | null;
  setSelectedStoryId: (id: number | null) => void;
  storyStatus: StoryStatus | null;
  storyStatusLoading: boolean;
  storyStatusError: string | null;
  isSwitchingStory?: boolean;
}

export const StoryProvider: React.FC<StoryProviderProps> = ({ 
  children, 
  selectedStoryId, 
  setSelectedStoryId,
  storyStatus,
  storyStatusLoading,
  storyStatusError,
  isSwitchingStory
}) => {
  const [fetchedDescriptions, setFetchedDescriptions] = useState<Set<string>>(new Set());

  return (
    <StoryContext.Provider value={{ 
      selectedStoryId, 
      setSelectedStoryId,
      fetchedDescriptions,
      setFetchedDescriptions,
      storyStatus,
      storyStatusLoading,
      storyStatusError,
      isSwitchingStory
    }}>
      {children}
    </StoryContext.Provider>
  );
};
